import { ReactComponent as CircledHeartSVG } from "@/assets/icons/circled-heart.svg";
import { ReactComponent as CircledTrophySVG } from "@/assets/icons/circled-trophy.svg";
import { ReactComponent as ExclamationSVG } from "@/assets/icons/exclamation.svg";
import { ReactComponent as PersonSVG } from "@/assets/icons/person.svg";
import { ReactComponent as ThumbSVG } from "@/assets/icons/thumb_up.svg";
import ageRestrictionImg from "@/assets/img/questions/age/age-restriction.png";
import i18n from "@/localization/i18next";
import { calculateWeight } from "@/utils/services/weightFormatters";

import { UnitTypes } from "./bodyMetrics";
import { Colors } from "./colors";
import { QUESTIONS_IDS } from "./questionsIds";

export const extraUnderweight = 16;
export const maxUnderweight = 18.5;
export const normal = 22;
export const maxNormal = 25;
export const maxObesity = 30;

const MIN_BMI = 18.5;
const MAX_BMI = 25;
const SM_IN_METER = 100;

export const enum BMI_STAGES {
    UNDERWEIGHT = "underweight",
    NORMAL = "normal",
    OVERWEIGHT = "overweight",
    OBESE = "obese",
}

export const bmiStagesLabels = {
    [BMI_STAGES.UNDERWEIGHT]: i18n.t("underweight"),
    [BMI_STAGES.NORMAL]: i18n.t("normal"),
    [BMI_STAGES.OVERWEIGHT]: i18n.t("overweight"),
    [BMI_STAGES.OBESE]: i18n.t("obese"),
};

export const BMI_RANGES = [
    {
        id: BMI_STAGES.UNDERWEIGHT,
        min: 12,
        max: 18.5,
    },
    {
        id: BMI_STAGES.NORMAL,
        min: 18.5,
        max: 25,
    },
    {
        id: BMI_STAGES.OVERWEIGHT,
        min: 25,
        max: 30,
    },
    {
        id: BMI_STAGES.OBESE,
        min: 30,
        max: 42,
    },
];

export const BMI_STAGES_ARR = [BMI_STAGES.UNDERWEIGHT, BMI_STAGES.NORMAL, BMI_STAGES.OVERWEIGHT];

export const getBMI = (weight: number, height: number) => {
    return Math.trunc((weight / (height * height)) * 100000) / 10;
};

export const calculateNormalWeight = (height: number) => {
    const heightInMeters = height / SM_IN_METER;
    const minWeight = Math.ceil(MIN_BMI * (heightInMeters * heightInMeters));
    const maxWeight = Math.floor(MAX_BMI * (heightInMeters * heightInMeters));

    return { minWeight, maxWeight };
};

const getStateByBmi = (bmi: number) => {
    if (bmi < maxUnderweight) return BMI_STAGES.UNDERWEIGHT;
    if (bmi < maxNormal) return BMI_STAGES.NORMAL;
    if (bmi < maxObesity) return BMI_STAGES.OVERWEIGHT;
    return BMI_STAGES.OBESE;
};

const getInfoTextByGoal = (
    goal: string,
    percentage: number,
    isFemale: boolean,
    isWeightLoss: boolean
) => {
    if (percentage < 10)
        return {
            title: i18n.t("easy_win_message", { goal, percentage }),
            text: i18n.t("study_of_5_min_workouts_message"),
            icon: () => <CircledTrophySVG fill={Colors.COOL_BLUE_500} color={Colors.WHITE} />,
            bgColor: isFemale ? Colors.COOL_BLUE_100 : Colors.COOL_BLUE_800,
        };
    if (percentage < 25)
        return {
            title: i18n.t("health_benefits_message", { goal, percentage }),
            text: isWeightLoss
                ? i18n.t("scientific_evidence_message")
                : i18n.t("study_of_5_min_workouts_message"),
            icon: () => <CircledHeartSVG fill={Colors.WARM_GREEN_500} color={Colors.WHITE} />,
            bgColor: isFemale ? Colors.WARM_GREEN_100 : Colors.WARM_GREEN_800,
        };
    return {
        title: i18n.t("challenging_goal_message", { goal, percentage }),
        text: isWeightLoss
            ? i18n.t("study_of_overweight_message")
            : i18n.t("study_of_5_min_workouts_message"),
        icon: () => <ThumbSVG fill={Colors.WARM_GREEN_500} color={Colors.WHITE} />,
        bgColor: isFemale ? Colors.WARM_GREEN_100 : Colors.WARM_GREEN_800,
    };
};

export const TEXT_BY_BODY_CONDITION = {
    [BMI_STAGES.UNDERWEIGHT]: i18n.t("improve_fitness_bmi"),
    [BMI_STAGES.NORMAL]: i18n.t("great_start"),
    [BMI_STAGES.OVERWEIGHT]: i18n.t("great_start"),
    [BMI_STAGES.OBESE]: i18n.t("improve_fitness_bmi"),
};

export const ICON_BY_BODY_CONDITION = {
    [BMI_STAGES.UNDERWEIGHT]: () => (
        <ExclamationSVG fill={Colors.WARM_YELLOW_500} color={Colors.WHITE} />
    ),
    [BMI_STAGES.NORMAL]: () => <ThumbSVG fill={Colors.WARM_GREEN_500} color={Colors.WHITE} />,
    [BMI_STAGES.OVERWEIGHT]: () => (
        <ExclamationSVG fill={Colors.WARM_YELLOW_500} color={Colors.WHITE} />
    ),
    [BMI_STAGES.OBESE]: () => <ExclamationSVG fill={Colors.WARM_RED_500} color={Colors.WHITE} />,
};

export const BG_COLOR_BY_BODY_CONDITION = (isFemale: boolean) => ({
    [BMI_STAGES.UNDERWEIGHT]: isFemale ? Colors.WARM_YELLOW_100 : Colors.WARM_YELLOW_800,
    [BMI_STAGES.NORMAL]: isFemale ? Colors.WARM_GREEN_100 : Colors.WARM_GREEN_800,
    [BMI_STAGES.OVERWEIGHT]: isFemale ? Colors.WARM_YELLOW_100 : Colors.WARM_YELLOW_800,
    [BMI_STAGES.OBESE]: isFemale ? Colors.WARM_RED_100 : Colors.WARM_RED_800,
});

interface BMIInfoProps {
    value: number;
    height: number;
    currentWeight: number;
    units: UnitTypes;
    isFemale: boolean;
    error?: string | null;
}

export const BMI_INFO_BY_QUESTION = {
    [QUESTIONS_IDS.HEIGHT]: ({ isFemale }: BMIInfoProps) => ({
        title: i18n.t("calculate_bmi"),
        text: i18n.t("bmi_definition"),
        icon: () => (
            <PersonSVG
                fill={isFemale ? Colors.BLACK : Colors.WHITE}
                color={isFemale ? Colors.WHITE : Colors.BLACK}
            />
        ),
        bgColor: isFemale ? Colors.WARM_TANGERINE_100 : Colors.COOL_BLUE_800,
    }),
    [QUESTIONS_IDS.CURRENT_WEIGHT]: ({ value, height, isFemale }: BMIInfoProps) => {
        const bmi = getBMI(value, height);
        const bodyCondition = getStateByBmi(bmi);
        return {
            title: i18n.t("your_bmi_is", { bmi, bodyCondition: i18n.t(bodyCondition) }),
            text: TEXT_BY_BODY_CONDITION[bodyCondition],
            icon: ICON_BY_BODY_CONDITION[bodyCondition],
            bgColor: BG_COLOR_BY_BODY_CONDITION(isFemale)[bodyCondition],
        };
    },
    [QUESTIONS_IDS.TARGET_WEIGHT]: ({
        value,
        height,
        currentWeight,
        units,
        isFemale,
    }: BMIInfoProps) => {
        const { minWeight, maxWeight } = calculateNormalWeight(height);

        if (value < minWeight) {
            return {
                title: i18n.t("low_weight_alert_title"),
                text: i18n.t("low_weight_alert_message", {
                    min: calculateWeight(minWeight, units),
                    max: calculateWeight(maxWeight, units),
                }),
                icon: () => <ExclamationSVG fill={Colors.WARM_RED_500} color={Colors.WHITE} />,
                bgColor: isFemale ? Colors.WARM_RED_100 : Colors.WARM_RED_800,
            };
        }
        const diff = Math.abs(value - currentWeight);
        const percentage = Math.trunc(((diff * 100) / (currentWeight * 100)) * 100);
        let isWeightLoss = false;
        if (value < currentWeight) {
            isWeightLoss = true;
        }
        const goal = isWeightLoss ? i18n.t("lose") : i18n.t("gain");
        return getInfoTextByGoal(goal, percentage, isFemale, isWeightLoss);
    },
    [QUESTIONS_IDS.AGE]: ({ isFemale, error }: BMIInfoProps) =>
        error
            ? {
                  text: (
                      <div className="flex items-center gap-2">
                          <img src={ageRestrictionImg} className="h-6 w-6" />
                          <div>{i18n.t("age_restriction_error")}</div>
                      </div>
                  ),
                  bgColor: isFemale ? Colors.WARM_TANGERINE_100 : Colors.WARM_RED_800,
              }
            : {
                  title: i18n.t("age_required_title"),
                  text: i18n.t("age_required_message"),
                  icon: () => (
                      <PersonSVG
                          fill={isFemale ? Colors.BLACK : Colors.WHITE}
                          color={isFemale ? Colors.WHITE : Colors.BLACK}
                      />
                  ),
                  bgColor: isFemale ? Colors.WARM_TANGERINE_100 : Colors.COOL_BLUE_800,
              },
};
